import { Signal, computed, signal } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

export type ID = string | number;

export function guid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
}

const registeredStores: BaseSignalStore<any>[] = [];

export function resetStores(): void {
  registeredStores.forEach(store => store.reset());
}

export function akitaConfig(_config?: any): void {
  // Compatibility no-op
}

export function combineQueries<T extends Observable<any>[]>(queries: [...T]): Observable<any> {
  return combineLatest(queries);
}

export function applyTransaction<T>(action: () => T): T {
  return action();
}

export function persistState(_config?: any): any {
  return {};
}

export function selectPersistStateInit(): Observable<boolean> {
  return of(true);
}

export class BaseSignalStore<T extends object> {
  private readonly _stateSignal = signal<T>(this.initialState);
  private readonly _subject: BehaviorSubject<T>;
  private _error: any = null;
  private _loading: boolean = false;
  readonly name: string;
  readonly state: Signal<T> = this._stateSignal.asReadonly();
  readonly data: Signal<any> = computed(() => (this._stateSignal() as any)?.data);

  constructor(
    readonly initialState: T,
    options?: { name?: string }
  ) {
    this.name = options?.name || '';

    let hydrated = initialState;
    if (this.name && typeof localStorage !== 'undefined') {
      try {
        const raw = localStorage.getItem('AkitaStores');
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && parsed[this.name]) {
            hydrated = { ...initialState, ...parsed[this.name] };
          }
        }
      } catch {
        // ignore localStorage error
      }
    }

    this._stateSignal.set(hydrated);
    this._subject = new BehaviorSubject<T>(hydrated);
    registeredStores.push(this);
  }

  private _persist(nextState: T): void {
    if (this.name && typeof localStorage !== 'undefined') {
      try {
        const raw = localStorage.getItem('AkitaStores');
        const allStores = raw ? JSON.parse(raw) : {};
        allStores[this.name] = nextState;
        localStorage.setItem('AkitaStores', JSON.stringify(allStores));
      } catch {
        // ignore storage errors
      }
    }
  }

  getValue(): T {
    return this._stateSignal();
  }

  update(updater: Partial<T> | any | ((state: T) => Partial<T> | any)): void {
    const current = this._stateSignal();
    const patch = typeof updater === 'function' ? updater(current) : updater;
    const nextState = { ...current, ...patch };
    this._stateSignal.set(nextState);
    this._subject.next(nextState);
    this._persist(nextState);
  }

  set(payload: any): void {
    this.update({ data: payload } as any);
  }

  updateData(payload: any): void {
    this.update(
      (state: any) =>
        ({
          ...state,
          data: { ...(state?.data || {}), ...payload }
        }) as any
    );
  }

  setError(error: any): void {
    this._error = error;
  }

  getError(): any {
    return this._error;
  }

  setLoading(loading: boolean): void {
    this._loading = loading;
  }

  getLoading(): boolean {
    return this._loading;
  }

  selectError(): Observable<any> {
    return of(this._error);
  }

  selectLoading(): Observable<boolean> {
    return of(this._loading);
  }

  reset(): void {
    this._stateSignal.set(this.initialState);
    this._subject.next(this.initialState);
    this._persist(this.initialState);
  }

  _select<R>(projector?: (state: T) => R): Observable<R | T> {
    if (projector) {
      return this._subject.asObservable().pipe(map(projector), distinctUntilChanged());
    }
    return this._subject.asObservable();
  }
}

export class BaseSignalQuery<T extends object> {
  constructor(protected store: BaseSignalStore<T>) {}

  getValue(): T {
    return this.store.getValue();
  }

  selectError(): Observable<any> {
    return this.store.selectError();
  }

  selectLoading(): Observable<boolean> {
    return this.store.selectLoading();
  }

  select(): Observable<T>;
  select<R>(projector: (state: T) => R): Observable<R>;
  select<R>(projector?: (state: T) => R): Observable<R | T> {
    return this.store._select(projector);
  }
}
